// src/rms/pricing/timeline.js
// ============================================================================
// Timeline geometry
// ============================================================================
// Pure functions turning instants into columns and bars. No React, no I/O, no
// availability logic — this module decides WHERE something is drawn and never
// whether it is bookable. That question has one owner, `availability.js`, and a
// second opinion here would be a second truth.
//
// ----------------------------------------------------------------------------
// The trade decides the column
// ----------------------------------------------------------------------------
// A day column is right for a car and actively misleading for a bicycle, which
// goes out three times in an afternoon. Every preset already carries
// `rateDefaults.primaryUnit`, so the grid reads that instead of branching on
// vertical_key — the same rule the rest of the codebase follows.
//
// The constant held across all twelve verticals is roughly 15–50 columns on
// screen. That is what keeps one component honest rather than four screens
// wearing a trench coat.
//
// ----------------------------------------------------------------------------
// Timezones, which is where every bug in this file will live
// ----------------------------------------------------------------------------
// A booking is an INSTANT. A day column is a CALENDAR DATE in the operator's
// timezone. Those are different kinds of thing, and conflating them is how a
// grid ends up one column out for half the year.
//
// So: day and week boundaries are stepped through local midnight in the
// operator's zone, which makes a 23-hour spring-forward day 23 hours wide and a
// 25-hour autumn day 25 — correct, and visible, rather than a silent drift.
// Hour and half-day columns step by fixed milliseconds; a DST shift inside a
// single day would misplace that day's later columns by an hour, which is a
// real limitation and a much smaller one than getting every day wrong.
// ============================================================================

const HOUR = 36e5;
const DAY = 864e5;

/**
 * Column size and default span for a trade's primary rate unit.
 *
 * `columns` is what the operator sees before scrolling; the numbers are chosen
 * so a bike shop sees two days and a storage operator sees half a year, and
 * both land in the same legible range.
 */
const GRANULARITY = {
  hour:     { unit: "hour",     units: ["hour", "half_day", "day"] },
  half_day: { unit: "half_day", units: ["hour", "half_day", "day", "week"] },
  day:      { unit: "day",      units: ["half_day", "day", "week", "month"] },
  week:     { unit: "day",      units: ["day", "week", "month"] },
  month:    { unit: "week",     units: ["day", "week", "month"] },
};

/**
 * How many columns of a given size to draw.
 *
 * Keyed on the COLUMN unit rather than on the trade, because it answers a
 * question about the column and not about the business: 48 hours is two days,
 * 26 weeks is half a year, 12 months is a lease. The trade picks where the
 * screen opens; the unit decides how much of it you see.
 */
const COLUMN_COUNT = {
  hour: 48,
  half_day: 28,
  day: 14,
  week: 26,
  month: 12,
};

export function granularityFor(primaryUnit) {
  return GRANULARITY[primaryUnit] || GRANULARITY.day;
}

/**
 * The column sizes worth offering a trade, coarsest last.
 *
 * Not the whole ladder. A self-storage operator on twelve-month leases was
 * being offered Hours and Half days -- 48 hours of a unit rented by the year --
 * while having no way to zoom out past 26 weeks, so a single lease could not
 * fit on screen. Both halves of that were wrong, and in opposite directions.
 *
 * The rule is the trade's own unit, one step finer for detail, and coarser for
 * planning. Storage gets days (a move-in), weeks, and months. A bike shop
 * never sees months.
 */
export function columnUnitsFor(primaryUnit) {
  return granularityFor(primaryUnit).units;
}

export function columnCountFor(unit) {
  return COLUMN_COUNT[unit] || COLUMN_COUNT.day;
}

/** Every unit a grid can be drawn in, coarsest last. */
export const COLUMN_UNITS = ["hour", "half_day", "day", "week", "month"];

// ----------------------------------------------------------------------------
// Timezone arithmetic
// ----------------------------------------------------------------------------

/**
 * How far the operator's wall clock is from UTC at a given instant.
 *
 * Derived by asking Intl what the clock reads there and subtracting, rather
 * than from a table — the browser already ships the whole tz database and any
 * offset we computed ourselves would be wrong the week a government moves a
 * DST boundary.
 */
function zoneOffsetMs(ms, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    })
      .formatToParts(new Date(ms))
      .reduce((acc, p) => (p.type === "literal" ? acc : { ...acc, [p.type]: Number(p.value) }), {});

    // hour comes back as 24 at midnight under hour12:false in some engines.
    const hour = parts.hour === 24 ? 0 : parts.hour;
    // Rounded to the minute. Intl reports whole seconds, so subtracting a
    // millisecond-precision instant leaves a sub-second remainder that would
    // put every day boundary a fraction off midnight -- and every blackout
    // created by dragging would be stamped 00:00:00.929 rather than midnight.
    // Every real UTC offset in the world is a whole number of minutes.
    const offset = Date.UTC(parts.year, parts.month - 1, parts.day, hour, parts.minute, parts.second) - ms;
    return Math.round(offset / 60000) * 60000;
  } catch {
    // An unknown zone must not blank the screen. UTC is wrong by hours; an
    // exception is wrong by the whole page.
    return 0;
  }
}

/** Midnight, in the operator's zone, of the day containing `ms`. */
export function startOfDay(ms, timeZone) {
  const offset = zoneOffsetMs(ms, timeZone);
  const floored = Math.floor((ms + offset) / DAY) * DAY - offset;
  // Recompute: if the floor crossed a DST boundary the first offset was the
  // wrong side of it. One correction is always enough — offsets change by an
  // hour, never by a day.
  const corrected = Math.floor((ms + zoneOffsetMs(floored, timeZone)) / DAY) * DAY
    - zoneOffsetMs(floored, timeZone);
  return corrected;
}

/** Midnight of the day `n` days after the one containing `ms`. */
export function addDays(ms, n, timeZone) {
  return startOfDay(startOfDay(ms, timeZone) + n * DAY + 12 * HOUR, timeZone);
}

/** The operator's local calendar date at an instant. */
function localDateParts(ms, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .formatToParts(new Date(ms))
    .reduce((acc, p) => (p.type === "literal" ? acc : { ...acc, [p.type]: Number(p.value) }), {});
}

/**
 * Midnight on the first of the month containing `ms`, in the operator's zone.
 *
 * Months are the one column that cannot be a fixed duration: they run 28 to 31
 * days, and a storage lease is quoted per calendar month, not per 30 days. So
 * this walks the calendar rather than the clock.
 *
 * Midday UTC on the target date is a safe landing point -- every zone offset is
 * within twelve hours of UTC, so noon is always inside the intended local day
 * -- and startOfDay then floors it correctly across any DST boundary.
 */
export function startOfMonth(ms, timeZone) {
  const { year, month } = localDateParts(ms, timeZone);
  return startOfDay(Date.UTC(year, month - 1, 1, 12), timeZone);
}

/** Midnight on the first of the month `n` months on. */
export function addMonths(ms, n, timeZone) {
  const { year, month } = localDateParts(ms, timeZone);
  // Date.UTC rolls month overflow into the year for us, in both directions.
  return startOfDay(Date.UTC(year, month - 1 + n, 1, 12), timeZone);
}

// ----------------------------------------------------------------------------
// Columns
// ----------------------------------------------------------------------------

/**
 * The columns of the grid, as half-open [startMs, endMs) ranges.
 *
 * Day and week columns are stepped through local midnight so they stay aligned
 * to the operator's calendar across a DST change. Hour and half-day columns
 * step by fixed duration from the start of their day.
 */
export function buildColumns({ anchorMs, unit, count, timeZone = "UTC" }) {
  const columns = [];
  // Month columns snap to the first of the month. A grid of months starting on
  // the 14th would label a column "August" while covering half of September.
  let cursor = unit === "month" ? startOfMonth(anchorMs, timeZone) : startOfDay(anchorMs, timeZone);

  for (let i = 0; i < count; i += 1) {
    let end;
    if (unit === "month") end = addMonths(cursor, 1, timeZone);
    else if (unit === "day") end = addDays(cursor, 1, timeZone);
    else if (unit === "week") end = addDays(cursor, 7, timeZone);
    else if (unit === "half_day") end = cursor + 12 * HOUR;
    else end = cursor + HOUR;

    columns.push({ startMs: cursor, endMs: end, unit });
    cursor = end;
  }
  return columns;
}

/** Milliseconds the whole visible window spans. */
export function windowBounds(columns) {
  if (!columns.length) return { startMs: 0, endMs: 0, spanMs: 1 };
  const startMs = columns[0].startMs;
  const endMs = columns[columns.length - 1].endMs;
  // Never zero: it is a divisor.
  return { startMs, endMs, spanMs: Math.max(1, endMs - startMs) };
}

// ----------------------------------------------------------------------------
// Bars
// ----------------------------------------------------------------------------

/**
 * Where a span sits in the window, as percentages.
 *
 * Positioned from the instants themselves, never from column indices: an
 * operator can set one asset to a different rate unit from the fleet default,
 * and snapping a 90-minute hire to a whole day column would misreport it as a
 * day out of service.
 *
 * A null `endMs` means open-ended — every storage occupancy, anything still
 * running — and runs to the right edge. `clippedEnd` lets the renderer show
 * that it continues rather than implying it stops there.
 */
export function spanGeometry({ startMs, endMs, windowStartMs, windowEndMs }) {
  const spanMs = Math.max(1, windowEndMs - windowStartMs);
  const rawStart = startMs ?? windowStartMs;
  const rawEnd = endMs ?? windowEndMs;

  if (rawEnd <= windowStartMs || rawStart >= windowEndMs) return null;

  const from = Math.max(rawStart, windowStartMs);
  const to = Math.min(rawEnd, windowEndMs);

  return {
    leftPct: ((from - windowStartMs) / spanMs) * 100,
    widthPct: Math.max(0.4, ((to - from) / spanMs) * 100),
    clippedStart: rawStart < windowStartMs,
    clippedEnd: endMs == null || rawEnd > windowEndMs,
    openEnded: endMs == null,
  };
}

// ----------------------------------------------------------------------------
// Gaps
// ----------------------------------------------------------------------------

/**
 * Free runs in a row, clipped to the window.
 *
 * The reason the screen exists: a list can show what is booked but never where
 * the space is. Runs shorter than one column are omitted — a nine-minute gap
 * between two hires is not an opportunity, it is a rounding artefact.
 */
export function gapsIn({ busy, windowStartMs, windowEndMs, minMs }) {
  const spans = busy
    .map((b) => ({
      start: Math.max(b.startMs, windowStartMs),
      end: Math.min(b.endMs ?? windowEndMs, windowEndMs),
    }))
    .filter((b) => b.end > b.start)
    .sort((a, b) => a.start - b.start);

  const gaps = [];
  let cursor = windowStartMs;

  for (const span of spans) {
    if (span.start > cursor) gaps.push({ startMs: cursor, endMs: span.start });
    cursor = Math.max(cursor, span.end);
  }
  if (cursor < windowEndMs) gaps.push({ startMs: cursor, endMs: windowEndMs });

  return gaps.filter((g) => g.endMs - g.startMs >= minMs);
}

/** Length of one column, for "is this gap worth mentioning". */
export function columnMs(unit) {
  // Nominal, and only ever used to decide whether a gap is worth mentioning --
  // never to place anything, which is done from real instants.
  if (unit === "month") return 30 * DAY;
  if (unit === "week") return 7 * DAY;
  if (unit === "day") return DAY;
  if (unit === "half_day") return 12 * HOUR;
  return HOUR;
}

export default {
  granularityFor,
  columnUnitsFor,
  columnCountFor,
  COLUMN_UNITS,
  startOfDay,
  startOfMonth,
  addDays,
  addMonths,
  buildColumns,
  windowBounds,
  spanGeometry,
  gapsIn,
  columnMs,
};
